import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ChevronLeft, ChevronRight, RefreshCw, AlertCircle, Rocket, Info } from 'lucide-react'
import { useDeployments, useCancelDeployment } from '@/hooks/useDeployments'
import { useProducts } from '@/hooks/useProducts'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useColumnPreferences } from '@/hooks/useColumnPreferences'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { EmptyState } from '@/components/common/EmptyState'
import { ColumnToggle } from '@/components/deployments/ColumnToggle'
import { COLUMNS, COLUMN_IDS, DEFAULT_VISIBLE_COLUMNS, nextOrdering } from '@/components/deployments/columns'
import { DeploymentDrawer } from '@/components/deployments/DeploymentDrawer'
import { DeploymentsFilters } from '@/components/deployments/DeploymentsFilters'
import { DeploymentsTable } from '@/components/deployments/DeploymentsTable'
import {
  EMPTY_FILTERS,
  type FilterChip,
  type FilterState,
  GROUPED_PAGE_SIZE,
  LIST_PAGE_SIZE,
  applyChip,
  hasActiveFilters,
  toQueryFilters,
} from '@/components/deployments/filters'
import { groupByCustomer } from '@/components/deployments/grouping'
import { type DeploymentsView, ViewToggle } from '@/components/deployments/ViewToggle'
import deploymentStyles from '@/components/deployments/deployments.module.css'
import type { DeploymentRequest } from '@/types'
import { parseApiError } from '@/utils/apiError'
import styles from './DeploymentsPage.module.css'

export function DeploymentsPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [chip, setChip] = useState<FilterChip>('all')
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState('')
  const [view, setView] = useState<DeploymentsView>('list')
  const [drawerId, setDrawerId] = useState<number | null>(null)

  const cancelMutation = useCancelDeployment()
  const { visible, toggle, reset } = useColumnPreferences(COLUMN_IDS, DEFAULT_VISIBLE_COLUMNS)
  const columns = COLUMNS.filter((c) => visible.includes(c.id))
  const { data: products } = useProducts()
  const productOptions = (products ?? []).filter((p) => p.parent === null).map((p) => ({ slug: p.slug, name: p.name }))

  const pageSize = view === 'customer' ? GROUPED_PAGE_SIZE : LIST_PAGE_SIZE
  const customer = useDebouncedValue(filters.customer, 300)
  const { data, isLoading, isError, refetch } = useDeployments(
    toQueryFilters({ filters: { ...filters, customer }, chip, ordering, page, pageSize }),
  )

  const allDeployments = data?.results ?? []
  const totalCount = data?.count ?? 0
  const totalPages = Math.ceil(totalCount / pageSize)
  const deployments = applyChip(allDeployments, chip)
  const groups = view === 'customer' ? groupByCustomer(deployments) : undefined
  const drawerDeployment = deployments.find((d) => d.id === drawerId) ?? null
  const filtered = hasActiveFilters(filters, chip)

  const updateFilters = (patch: Partial<FilterState>) => {
    setFilters((current) => ({ ...current, ...patch }))
    setPage(1)
  }

  const handleChipChange = (next: FilterChip) => {
    setChip(next)
    setFilters((current) => ({ ...current, status: '' }))
    setPage(1)
  }

  const handleCancel = (dep: DeploymentRequest) => {
    if (window.confirm('Cancel this deployment?')) {
      cancelMutation.mutate(dep.id, {
        // WorkWise cancels can be refused (409); say why. Other products behave as before.
        onError: (error) => {
          if (dep.workwise) window.alert(parseApiError(error).message)
        },
      })
    }
  }

  // WorkWise rows open the drawer; every other row goes straight to its detail page, as before.
  const handleRowClick = (dep: DeploymentRequest) => {
    if (dep.workwise) setDrawerId(dep.id)
    else navigate(`/deployments/${dep.id}`)
  }

  if (isError) {
    return (
      <div className={styles.page}>
        <EmptyState
          icon={<AlertCircle size={48} />}
          title="Failed to load deployments"
          description="Could not fetch deployments from the API."
          action={
            <Button onClick={() => refetch()} icon={<RefreshCw size={14} />}>
              Retry
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Deployments"
        subtitle="Manage and monitor all deployment requests"
        actions={
          <Button onClick={() => navigate('/deployments/new')} icon={<Plus size={16} />}>
            New Deployment
          </Button>
        }
      />

      <DeploymentsFilters
        filters={filters}
        chip={chip}
        products={productOptions}
        onChange={updateFilters}
        onChipChange={handleChipChange}
      />

      <div className={deploymentStyles.toolbar}>
        <ViewToggle
          value={view}
          onChange={(next) => {
            setView(next)
            setPage(1)
          }}
        />
        <ColumnToggle visible={visible} onToggle={toggle} onReset={reset} />
      </div>

      {view === 'customer' && (
        <div className={deploymentStyles.banner} role="status" data-testid="grouping-banner">
          <Info size={14} />
          <span>
            Customers are grouped within the current page only (up to {GROUPED_PAGE_SIZE} rows).
            {totalCount > allDeployments.length && ` Showing ${allDeployments.length} of ${totalCount}; use the page controls for the rest.`}
          </span>
        </div>
      )}

      <Card>
        {isLoading ? (
          <LoadingSpinner label="Loading deployments..." />
        ) : deployments.length === 0 ? (
          <EmptyState
            icon={<Rocket size={36} />}
            title="No deployments found"
            description={filtered ? 'Try adjusting your filters.' : 'No deployments have been created yet.'}
            action={
              !filtered ? (
                <Button onClick={() => navigate('/deployments/new')} icon={<Plus size={14} />}>
                  New Deployment
                </Button>
              ) : undefined
            }
          />
        ) : (
          <DeploymentsTable
            columns={columns}
            rows={deployments}
            groups={groups}
            ordering={ordering}
            onSort={(key) => {
              setOrdering(nextOrdering(ordering, key))
              setPage(1)
            }}
            onRowClick={handleRowClick}
            onView={(dep) => navigate(`/deployments/${dep.id}`)}
            onCancel={handleCancel}
            cancellingId={cancelMutation.isPending ? (cancelMutation.variables ?? null) : null}
          />
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
            </span>
            <div className={styles.paginationControls}>
              <Button
                variant="secondary"
                size="sm"
                icon={<ChevronLeft size={14} />}
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              />
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const pageNum = i + 1
                return (
                  <button
                    key={pageNum}
                    className={`${styles.pageBtn} ${page === pageNum ? styles.pageBtnActive : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <Button
                variant="secondary"
                size="sm"
                icon={<ChevronRight size={14} />}
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </Card>

      <DeploymentDrawer
        deployment={drawerDeployment}
        onClose={() => setDrawerId(null)}
        onOpenDetails={(dep) => navigate(`/deployments/${dep.id}`)}
      />
    </div>
  )
}
